import React, { useState } from 'react';
import { Mail, MapPin, Send } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import toast from 'react-hot-toast';
import { websiteApi, ContactFormData } from '@/api/website';
import PublicShell from '@/components/layouts/PublicShell';

const ContactPage = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    urgency: 'normal'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await websiteApi.contact.submit(formData);
      toast.success('Ihre Nachricht wurde erfolgreich gesendet!');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          urgency: 'normal'
        });
    } catch (error) {
      toast.error('Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicShell>
      {/* Hero Banner */}
      <div className="relative bg-primary-950 overflow-hidden">
        <img src="/images/sv-office-building.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20 md:py-24">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-gold-400 mb-4 block">Sky View GmbH</span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-medium text-white mb-6 leading-tight">
              Kontakt
            </h1>
            <div className="w-16 h-[3px] bg-gold-500 mb-6"></div>
            <p className="text-base sm:text-lg text-primary-200 leading-relaxed">
              Wir sind hier, um Ihnen zu helfen. Kontaktieren Sie uns für alle Ihre Finanzfragen.
            </p>
          </div>
        </div>
      </div>

      {/* Three Cards Section */}
      <div className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
              {/* Card 1 - Address */}
              <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6 sm:p-8">
                <div className="w-11 h-11 bg-primary-50 flex items-center justify-center mb-6">
                  <MapPin className="w-5 h-5 text-primary-700" />
                </div>
                <h3 className="text-lg font-serif font-medium text-slate-900 mb-3">Anschrift</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Sky View GmbH<br/>
                  Bockenheimer Landstraße 17/19<br/>
                  60325 Frankfurt am Main<br/>
                  Deutschland
                </p>
              </div>


              {/* Card 2 - Email */}
              <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6 sm:p-8">
                <div className="w-11 h-11 bg-primary-50 flex items-center justify-center mb-6">
                  <Mail className="w-5 h-5 text-primary-700" />
                </div>
                <h3 className="text-lg font-serif font-medium text-slate-900 mb-3">E-Mail</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  <a href="mailto:info@skyview-assets.de" className="text-primary-700 hover:text-gold-600 transition-colors">info@skyview-assets.de</a><br/>
                  Wir antworten innerhalb<br/>
                  von 24 Stunden
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="bg-primary-50/40 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-slate-200 border-t-[3px] border-t-primary-600 p-6 sm:p-10">
              <h2 className="text-2xl md:text-3xl font-serif font-medium text-slate-900 mb-8 text-center">
                Senden Sie uns eine Nachricht
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                      Betreff *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  ></textarea>
                </div>

                <div className="text-center">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primary-800 hover:bg-primary-900 text-white px-8 py-3.5 text-base font-semibold disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Senden...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Nachricht senden
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </PublicShell>
  );
};

export default ContactPage;
